import { useMutation, useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import { useEffect, useRef, useState } from "react";
import { TailSpin } from "react-loader-spinner";
import {
  type LoaderFunction,
  useLoaderData,
  useNavigate,
  useRevalidator,
  useSearchParams,
} from "react-router-dom";

import {
  deletePlatformUserMembershipsByPlatformUserMembershipIdChangeMembership,
  deletePlatformUserMembershipsByPlatformUserMembershipIdFreezeRequestsByMembershipFreezeId,
  getOrdersCurrentSummary,
  getPlatformLocation,
  getPlatformUserMemberships,
  getPlatformUserMembershipsByPlatformUserMembershipIdFreeze,
  getPlatformUserMembershipsStatus,
  postOrdersCurrentLineitems,
  postPlatformUserMembershipsByPlatformUserMembershipIdReactivate,
  putPlatformUserMembershipsByPlatformUserMembershipIdFreezeByMembershipFreezeIdResume,
  putPlatformUserMembershipsByPlatformUserMembershipIdKeepMembership,
  putPlatformUserMembershipsByPlatformUserMembershipIdRetryMembershipPayment,
} from "api/methods";
import { useTranslation } from "@/core/i18n";
import ChevronLeft from "atoms/icon/icons/chevron_left.svg";
import BillingInfo from "atoms/icon/icons/ic_billing_info_small_ol.svg";
import { Notification, useNotify } from "atoms/notification";
import { RepeatMDFooter } from "atoms/repeatmd-footer/RepeatMDFooter";
import { PAYMENT_UPDATED_PARAM, UPDATED_MEMBERSHIP_ID_PARAM } from "constants/index";
import { ManageMembershipCard } from "molecules/cards/manage-membership-card/ManageMembershipCard";
import { ActionModal } from "molecules/modal-window/action-modal/ActionModal";
import TopNav from "molecules/navbars/TopNav";
import { queryClient } from "pages/Root";
import { useFeatureFlag } from "services/Flags";
import { formatCurrency } from "toolbox/Money";
import { waitObj } from "toolbox/Promise";
import { hasCancellationRunway } from "utils/membershipStatus";

import {
  get4xxErrorMessage,
  invalidateMembershipFreezeQueries,
  invalidateMembershipQueries,
  isAwaitingPaymentMethodSwap,
  isFreezeScheduled,
  isManualPaymentRetryInProgressError,
  shouldReactivateScheduledCancellation,
} from "./membershipHelper";
import { RetryMembershipPaymentFailedModal } from "./RetryMembershipPaymentFailedModal";
import { useManualPaymentRetryCooldown } from "./useManualPaymentRetryCooldown";

const PAYMENT_METHOD_SWAP_POLL_INTERVAL = 2000;
const PAYMENT_METHOD_SWAP_TIMEOUT = 30_000;

const load = async () => {
  const userMemberships = queryClient.fetchQuery({ ...getPlatformUserMemberships.keyed() });
  const userMembershipsStatus = queryClient.fetchQuery({
    ...getPlatformUserMembershipsStatus.keyed(),
  });

  const platformLocation = queryClient.fetchQuery({ ...getPlatformLocation.keyed() });

  const data = await waitObj({
    platformLocation,
    userMemberships,
    userMembershipsStatus,
  });

  const membershipFreezes = Object.fromEntries(
    await Promise.all(
      data.userMemberships
        .filter(({ activeMembershipFreezeId }) => activeMembershipFreezeId)
        .map(async ({ platformUserMembershipId }) => {
          // A 204 (no in-flight freeze) is returned as `null` by the shared `get`
          // helper; genuine failures propagate to the route error boundary.
          const freeze = await queryClient.fetchQuery({
            ...getPlatformUserMembershipsByPlatformUserMembershipIdFreeze.keyed({
              platformUserMembershipId,
            }),
          });

          return [platformUserMembershipId, freeze] as const;
        }),
    ),
  );

  return { ...data, membershipFreezes };
};

export const prefetchWalletMembershipManage = () => {
  void load().catch(() => undefined);
};

type LoaderData = Awaited<ReturnType<typeof load>>;
export const walletMembershipManageLoader: LoaderFunction = () => {
  return load();
};

export const MembershipPaymentMethodConfirmationModal = ({
  platformUserMembershipId,
  onClose,
}: {
  platformUserMembershipId: string;
  onClose: () => void;
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <ActionModal
      size="small"
      open
      title={t("walletManageMembershipsPage.updatePaymentMethodTitle")}
      description={t("walletManageMembershipsPage.updatePaymentMethodDescription")}
      firstButtonLabel={t("walletManageMembershipsPage.updatePaymentMethodButton")}
      onFirstButtonClick={() =>
        navigate(`/wallet/memberships/manage/${platformUserMembershipId}/payment-method`)
      }
      headerIconSvg={BillingInfo}
      secondButtonLabel={t("common.cancel")}
      onSecondButtonClick={onClose}
      onClose={onClose}
    />
  );
};

export const WalletMembershipsManagePage = () => {
  const { t } = useTranslation();
  const { platformLocation, ...data } = useLoaderData() as LoaderData;

  const navigate = useNavigate();
  const { revalidate } = useRevalidator();
  const [searchParams, setSearchParams] = useSearchParams();
  const [notificationProps, notify] = useNotify();

  /**
   * If `updated_membership` search param is present in the
   * URL and the updated membership it points to is in dunning,
   * it means that the payment method was successfully
   * updated which triggered automatic payment retry on the
   * backend. Knowing that, we switch on the polling mode &
   * wait for the membership to update.
   */
  const paymentMethodUpdateStatus = searchParams.get(PAYMENT_UPDATED_PARAM);
  const updatedMembershipId = searchParams.get(UPDATED_MEMBERSHIP_ID_PARAM);
  const updatedMembership = data.userMemberships.find(
    ({ platformUserMembershipId }) => platformUserMembershipId === updatedMembershipId,
  );
  const initialUpdatedMembership =
    updatedMembership && updatedMembership.dunningStage > 0 && updatedMembership.membership.active
      ? updatedMembership
      : undefined;
  const [isPolling, setIsPolling] = useState(
    paymentMethodUpdateStatus === "1" && !!initialUpdatedMembership,
  );
  const [platformUserMembershipIdToRepay, setPlatformUserMembershipIdToRepay] = useState(
    initialUpdatedMembership?.platformUserMembershipId || "",
  );
  /**
   * Outside dunning there is no payment retry to wait for, but the new card still lands
   * asynchronously: Stripe confirms it in the browser and the backend only learns about it
   * from the `setup_intent.succeeded` webhook, so the memberships fetched on the way back
   * here can still carry the card being replaced. Hold that membership and keep refetching
   * until the API reports a different card, so the page updates without a reload.
   */
  const [membershipAwaitingPaymentMethodSwap, setMembershipAwaitingPaymentMethodSwap] = useState(
    paymentMethodUpdateStatus === "1" && !initialUpdatedMembership ? updatedMembership : undefined,
  );
  const isPaymentMethodUpdateNotificationsSilencedRef = useRef(false);

  const [showModalId, setShowModalId] = useState<
    | "updatePaymentMethod"
    | "keepMembership"
    | "retryMembershipPaymentFailed"
    | "resumeMembership"
    | undefined
  >(undefined);
  const [modalMembershipId, setModalMembershipId] = useState<string | undefined>(undefined);
  const [modalFreezeId, setModalFreezeId] = useState<string | undefined>(undefined);
  const retryCooldown = useManualPaymentRetryCooldown();

  const isFlexibleMembershipsEnabled = useFeatureFlag("flexible_membership_terms");

  const { data: userMemberships } = useQuery({
    ...getPlatformUserMemberships.keyed(),
    initialData: data.userMemberships,
    refetchInterval: (query) => {
      const userMemberships = query.state.data;
      const userMembershipToRepay = userMemberships?.find(
        ({ platformUserMembershipId }) =>
          platformUserMembershipId === platformUserMembershipIdToRepay,
      );
      const userMembershipOnDunning = userMemberships?.some(
        ({ canceled, dunningStage }) => !canceled && dunningStage,
      );

      if (
        (!userMembershipToRepay && !userMembershipOnDunning) ||
        userMembershipToRepay?.dunningStage === 0
      ) {
        return isAwaitingPaymentMethodSwap(userMemberships, membershipAwaitingPaymentMethodSwap)
          ? PAYMENT_METHOD_SWAP_POLL_INTERVAL
          : false;
      }

      return userMembershipToRepay ? 1000 : 2000;
    },
  });

  // This API call is updated to know if the user is in dunning stage or has updated payment method
  const { data: statuses } = useQuery({
    ...getPlatformUserMembershipsStatus.keyed(),
    initialData: data.userMembershipsStatus,
    // Membership cancellation happens async so we need to poll the data for status updates
    refetchInterval: (query) => {
      const statuses = query.state.data;
      return statuses?.some(({ canceled, dunningStage }) => !canceled && dunningStage)
        ? 2000
        : false;
    },
  });

  const hideModal = () => {
    setModalMembershipId(undefined);
    setModalFreezeId(undefined);
    setShowModalId(undefined);
  };

  const keepMembershipMutation = useMutation({
    mutationFn: (platformUserMembershipId: string) =>
      putPlatformUserMembershipsByPlatformUserMembershipIdKeepMembership({
        platformUserMembershipId,
      }),
    onSuccess: async () => {
      await invalidateMembershipQueries(queryClient);

      hideModal();
      notify.success(t("walletManageMembershipsPage.membershipChangesSaved"), {
        timeout: 5000,
        dismissable: false,
      });
    },
    onError: () => {
      notify.error(t("walletManageMembershipsPage.genericError"), {
        timeout: 5000,
        dismissable: false,
      });
    },
  });

  const reactivateScheduledMembershipMutation = useMutation({
    mutationFn: (platformUserMembershipId: string) =>
      postPlatformUserMembershipsByPlatformUserMembershipIdReactivate({
        platformUserMembershipId,
      }),
    onSuccess: async () => {
      await invalidateMembershipQueries(queryClient);

      hideModal();
      notify.success(t("walletManageMembershipsPage.membershipChangesSaved"), {
        timeout: 5000,
        dismissable: false,
      });
    },
    onError: () => {
      notify.error(t("walletManageMembershipsPage.genericError"), {
        timeout: 5000,
        dismissable: false,
      });
    },
  });

  const keepCurrentMembershipMutation = useMutation({
    mutationFn: (platformUserMembershipId: string) =>
      deletePlatformUserMembershipsByPlatformUserMembershipIdChangeMembership({
        platformUserMembershipId,
      }),
    onSuccess: async () => {
      await invalidateMembershipQueries(queryClient);

      notify.success(t("walletManageMembershipsPage.keptCurrentMembership"), {
        timeout: 5000,
        dismissable: false,
      });
    },
    onError: () => {
      notify.error(t("walletManageMembershipsPage.genericError"), {
        timeout: 5000,
        dismissable: false,
      });
    },
  });

  const addCanceledMembershipToCartMutation = useMutation({
    mutationFn: (membershipId: string) =>
      postOrdersCurrentLineitems({
        membershipId,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: getOrdersCurrentSummary.key({}) });

      navigate("/cart");
    },
  });

  const withdrawFreezeRequestMutation = useMutation({
    mutationFn: ({
      platformUserMembershipId,
      membershipFreezeId,
    }: {
      platformUserMembershipId: string;
      membershipFreezeId: string;
    }) =>
      deletePlatformUserMembershipsByPlatformUserMembershipIdFreezeRequestsByMembershipFreezeId({
        platformUserMembershipId,
        membershipFreezeId,
      }),
    onSuccess: async () => {
      await invalidateMembershipFreezeQueries(queryClient);

      revalidate();
      notify.success("Freeze request withdrawn.", {
        timeout: 5000,
        dismissable: false,
      });
    },
    onError: () => {
      notify.error("Something went wrong - please contact support if this issue persists.", {
        timeout: 5000,
        dismissable: false,
      });
    },
  });

  const resumeMembershipMutation = useMutation({
    mutationFn: ({
      platformUserMembershipId,
      membershipFreezeId,
    }: {
      platformUserMembershipId: string;
      membershipFreezeId: string;
      /** Freeze approved but not started yet — resuming cancels it without charging. */
      isScheduledFreeze: boolean;
    }) =>
      putPlatformUserMembershipsByPlatformUserMembershipIdFreezeByMembershipFreezeIdResume({
        platformUserMembershipId,
        membershipFreezeId,
      }),
    onSuccess: async (_, { isScheduledFreeze }) => {
      await invalidateMembershipFreezeQueries(queryClient);

      revalidate();
      hideModal();
      notify.success(
        isScheduledFreeze
          ? "Your scheduled freeze has been canceled."
          : "Your membership has resumed.",
        {
          timeout: 5000,
          dismissable: false,
        },
      );
    },
    onError: (error) => {
      hideModal();
      notify.error(
        get4xxErrorMessage(error) ??
          "Something went wrong - please contact support if this issue persists.",
        {
          timeout: 5000,
          dismissable: false,
        },
      );
    },
  });

  const retryMembershipPaymentMutation = useMutation({
    mutationFn: (platformUserMembershipId: string) =>
      putPlatformUserMembershipsByPlatformUserMembershipIdRetryMembershipPayment({
        platformUserMembershipId,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: getPlatformUserMemberships.key(),
      });

      setIsPolling(true);
    },
    onError: (error, platformUserMembershipId) => {
      setPlatformUserMembershipIdToRepay("");

      // Turned away because a retry for this membership is already running (another tab, or the
      // retry the backend runs itself after a payment-method update): nothing was attempted on the
      // member's card and the other attempt may be succeeding, so this is not a payment failure.
      // The claim window is still open on the server, so hold the action for a full window again.
      if (isManualPaymentRetryInProgressError(error)) {
        retryCooldown.start(platformUserMembershipId);
        notify.info(t("walletManageMembershipsPage.retryPaymentAlreadyInProgress"), {
          timeout: 5000,
          dismissable: false,
        });
        return;
      }

      setShowModalId("retryMembershipPaymentFailed");
    },
  });

  /**
   * Cancel polling mode after a fixed timeout.
   */
  useEffect(() => {
    if (!isPolling) {
      return;
    }

    const timeoutId = setTimeout(() => {
      setIsPolling(false);
      setPlatformUserMembershipIdToRepay("");
      setSearchParams();
      isPaymentMethodUpdateNotificationsSilencedRef.current = true;
      notify.error(t("walletManageMembershipsPage.genericError"), {
        timeout: 5000,
        dismissable: false,
      });
    }, 60_000);
    return () => {
      clearTimeout(timeoutId);
    };
  }, [isPolling, notify, t]);

  /**
   * Unset polling mode when polling is complete.
   */
  useEffect(() => {
    const userMembershipToRepay = userMemberships.find(
      ({ platformUserMembershipId }) =>
        platformUserMembershipId === platformUserMembershipIdToRepay,
    );

    if (isPolling && (!userMembershipToRepay || userMembershipToRepay.dunningStage === 0)) {
      isPaymentMethodUpdateNotificationsSilencedRef.current = true;
      setIsPolling(false);
      setPlatformUserMembershipIdToRepay("");
      setSearchParams();
      notify.success(t("walletManageMembershipsPage.paymentSuccessful"), {
        timeout: 5000,
        dismissable: false,
      });
    }
  }, [isPolling, notify, platformUserMembershipIdToRepay, userMemberships, t]);

  /**
   * Syncs the URL with the poll outcome: once the API reports the new card, or the wait window
   * closes, drop the `payment_updated` params so a refresh does not start the wait over. The
   * outcome arrives from a refetch rather than a user action, so no handler can own it.
   */
  useEffect(() => {
    if (!membershipAwaitingPaymentMethodSwap) {
      return;
    }

    const stopAwaitingPaymentMethodSwap = () => {
      setMembershipAwaitingPaymentMethodSwap(undefined);
      setSearchParams({}, { replace: true });
    };

    if (!isAwaitingPaymentMethodSwap(userMemberships, membershipAwaitingPaymentMethodSwap)) {
      stopAwaitingPaymentMethodSwap();
      return;
    }

    const timeoutId = setTimeout(stopAwaitingPaymentMethodSwap, PAYMENT_METHOD_SWAP_TIMEOUT);
    return () => {
      clearTimeout(timeoutId);
    };
  }, [membershipAwaitingPaymentMethodSwap, setSearchParams, userMemberships]);

  useEffect(() => {
    if (isPaymentMethodUpdateNotificationsSilencedRef.current) {
      return;
    }

    if (paymentMethodUpdateStatus === "1") {
      notify.success(t("walletManageMembershipsPage.paymentMethodUpdatedSuccess"), {
        timeout: 5000,
        dismissable: false,
      });
    } else if (paymentMethodUpdateStatus === "0") {
      notify.error(t("walletManageMembershipsPage.paymentMethodUpdateFailed"), {
        timeout: 5000,
        dismissable: false,
      });
    }
  }, [notify, paymentMethodUpdateStatus, t]);

  const modalMembership = userMemberships?.find(
    ({ platformUserMembershipId }) => platformUserMembershipId === modalMembershipId,
  );
  const modalFreeze = modalMembershipId ? data.membershipFreezes[modalMembershipId] : undefined;
  // An approved freeze that hasn't started yet: "resuming" cancels the scheduled
  // freeze without charging — the backend does not re-anchor billing in that case.
  const isModalFreezeScheduled = isFreezeScheduled(modalFreeze);

  const activeMemberships = userMemberships.filter(hasCancellationRunway).length;

  if (isPolling) {
    return (
      <div className="flex flex-col items-center gap-8 px-8 pb-8 pt-[calc(20px_+_env(safe-area-inset-top))]">
        <TailSpin height="80" width="80" radius="1" color="rgb(var(--repeatmd-brand-color))" />
        <div className="pb-12 text-center font-serif text-h2">
          <div>{t("walletManageMembershipsPage.loadingMoment")}</div>
          <div>{t("walletManageMembershipsPage.retryingPayment")}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative flex h-full flex-col bg-one">
      <Notification {...notificationProps} />
      <TopNav
        startIconSVG={ChevronLeft}
        showHeaderItems={false}
        onStartIconClick={() => navigate(-1)}
      >
        <div className="flex justify-center text-sub2">
          {t("walletManageMembershipsPage.header")}
        </div>
      </TopNav>
      <div className="flex flex-col gap-y-4 pt-4 text-center">
        <div className="text-center text-body2 text-secondary">
          {t("walletManageMembershipsPage.activeMembershipsCount", { count: activeMemberships })}
        </div>
        <div className="flex flex-col gap-y-4 px-4">
          {userMemberships.map((item) => {
            const membershipStatus = statuses.find(({ id }) => id == item.id);

            if (!membershipStatus) {
              return null;
            }

            return (
              <ManageMembershipCard
                key={item.id}
                platformUserMembership={item}
                disableCancellationAllowed={platformLocation.supportsMinimumCommitmentPeriod}
                paymentMethodLast4={item.paymentMethodDetails?.last4}
                onReactivateMembership={() => {
                  addCanceledMembershipToCartMutation.mutate(item.membership.id);
                }}
                onUpdatePaymentMethod={() => {
                  setModalMembershipId(item.platformUserMembershipId);
                  setShowModalId("updatePaymentMethod");
                }}
                onKeepMembership={() => {
                  if (shouldReactivateScheduledCancellation(membershipStatus)) {
                    reactivateScheduledMembershipMutation.mutate(item.platformUserMembershipId);
                    return;
                  }

                  setModalMembershipId(item.platformUserMembershipId);
                  setShowModalId("keepMembership");
                }}
                membershipStatus={membershipStatus}
                onKeepCurrentMembership={(platformUserMembershipId) => {
                  keepCurrentMembershipMutation.mutate(platformUserMembershipId);
                }}
                isKeepingCurrentMembership={
                  keepCurrentMembershipMutation.isPending &&
                  keepCurrentMembershipMutation.variables === item.platformUserMembershipId
                }
                freeze={data.membershipFreezes[item.platformUserMembershipId]}
                onWithdrawFreezeRequest={(platformUserMembershipId, membershipFreezeId) => {
                  withdrawFreezeRequestMutation.mutate({
                    platformUserMembershipId,
                    membershipFreezeId,
                  });
                }}
                withdrawPending={
                  withdrawFreezeRequestMutation.isPending &&
                  withdrawFreezeRequestMutation.variables?.platformUserMembershipId ===
                    item.platformUserMembershipId
                }
                onResumeMembership={(platformUserMembershipId, membershipFreezeId) => {
                  setModalMembershipId(platformUserMembershipId);
                  setModalFreezeId(membershipFreezeId);
                  setShowModalId("resumeMembership");
                }}
                onRetryMembershipPayment={() => {
                  if (!item.platformUserMembershipId) {
                    return;
                  }

                  setPlatformUserMembershipIdToRepay(item.platformUserMembershipId);
                  setModalMembershipId(item.platformUserMembershipId);
                  retryCooldown.start(item.platformUserMembershipId);
                  retryMembershipPaymentMutation.mutate(item.platformUserMembershipId);
                }}
                isRetryingMembershipPayment={
                  (retryMembershipPaymentMutation.isPending &&
                    retryMembershipPaymentMutation.variables === item.platformUserMembershipId) ||
                  retryCooldown.isActive(item.platformUserMembershipId)
                }
              />
            );
          })}
        </div>
      </div>
      <div className="grow" />
      <div className="flex w-full scroll-py-4 flex-col items-center">
        <RepeatMDFooter />
      </div>
      {showModalId === "updatePaymentMethod" && modalMembershipId && (
        <MembershipPaymentMethodConfirmationModal
          platformUserMembershipId={modalMembershipId}
          onClose={hideModal}
        />
      )}
      {showModalId === "keepMembership" && modalMembershipId && (
        <ActionModal
          size="small"
          open
          title={t("walletManageMembershipsPage.keepMembershipTitle")}
          description={t("walletManageMembershipsPage.keepMembershipDescription", {
            membershipName: modalMembership?.membership?.name,
            expirationDate: dayjs(modalMembership?.expiration).format("MMM D, YYYY"),
          })}
          firstButtonLabel={t("common.confirm")}
          firstButtonDisabled={keepMembershipMutation.isPending}
          onFirstButtonClick={() => keepMembershipMutation.mutate(modalMembershipId)}
          secondButtonLabel={t("common.cancel")}
          onSecondButtonClick={hideModal}
          onClose={hideModal}
        />
      )}
      {showModalId === "resumeMembership" &&
        modalMembershipId &&
        modalFreezeId &&
        modalMembership && (
          <ActionModal
            size="small"
            open
            title={
              isModalFreezeScheduled
                ? "Cancel your scheduled freeze?"
                : "Resume your membership now?"
            }
            description={
              isModalFreezeScheduled && modalFreeze
                ? `Your ${modalMembership.membership.name} membership keeps billing as normal, and the scheduled freeze on ${dayjs(
                    modalFreeze.startsAt,
                  ).format("MMM D, YYYY")} is canceled. You won't be charged anything extra.`
                : `Your ${modalMembership.membership.name} membership resumes immediately. You'll be charged ${formatCurrency(
                    isFlexibleMembershipsEnabled
                      ? modalMembership.membership.billingPeriodCost
                      : modalMembership.membership.monthlyCost,
                  )} today, and your billing date moves to today.`
            }
            firstButtonLabel={
              isModalFreezeScheduled ? "Cancel scheduled freeze" : "Resume membership"
            }
            firstButtonDisabled={resumeMembershipMutation.isPending}
            onFirstButtonClick={() =>
              resumeMembershipMutation.mutate({
                platformUserMembershipId: modalMembershipId,
                membershipFreezeId: modalFreezeId,
                isScheduledFreeze: isModalFreezeScheduled,
              })
            }
            secondButtonLabel={isModalFreezeScheduled ? "Keep my freeze" : "Cancel"}
            onSecondButtonClick={hideModal}
            onClose={hideModal}
          />
        )}
      {showModalId === "retryMembershipPaymentFailed" && modalMembershipId && (
        <RetryMembershipPaymentFailedModal
          paymentMethodLast4={modalMembership?.paymentMethodDetails?.last4}
          onClose={hideModal}
        />
      )}
    </div>
  );
};
